import { createSelector, OutputParametricSelector } from "reselect";

import type {
  PriceValidation,
  SizeValidation,
} from "../store/state/exchange-betting-potential/ExchangeBettingPotential.types";
import { LADDER_DEFAULT_MIN_SIZE } from "../config/bet-engine-config";
import { ApplicationState } from "@ppb/tbd-store/state/ApplicationState.types";
import { BetslipApplicationState } from "../store/state/BetslipApplicationState";
import { getBetslipCastContext, getBetslipGroup } from "../store/state/betslip-module/betslip-module-selectors";
import { BettingState } from "@ppb/betslip-core";
import { formatOdds } from "@ppb/formatters";
import { CastGroup } from "@ppb/tbd-store/helpers/sportsbook-betting";
import {
  BettingMetadata,
  RunnersMetadata,
} from "@ppb/tbd-store/state/betting/sportsbook-betting/SportsbookBetting.types";
import { getSportsbookBettingState } from "@ppb/tbd-store/state/betting/sportsbook-betting/sportsbook-betting-selectors";
import { getBettingResolvers } from "../store/state/sportsbook-betting/betting-resolver";
import { createGetCastGroupSelector } from "../store/state/sportsbook-betting/sportsbook-betting-selectors";
import type { BetslipExchangeMarketStatus } from "../store/state/exchange-markets/ExchangeMarkets.types";
import { BetslipCastContext, BetslipSportsbookConfirmationBet } from "@ppb/tbd-store/state/betslip/Betslip.types";
import type { CastBetRunnerSelectionProps } from "./SportsbookReceipt/snowflakes/SportsbookReceiptPanel/SportsbookReceiptPanel.types";
import { ExchangeBetTransactionError } from "../services/ExchangeBetTransactionError.types";
import { CastBetCastTypeItem, ForecastTricastSelectionProps, AlertProps } from "@ppb/the-wall-common/types";
import { getObbBettingState } from "@ppb/tbd-store/state/betting/obb-betting/obb-betting-selectors";

import { currencyFormatWithDecimalPlaces, currencyFormatWithoutDecimalPlaces } from "../formatters/currency-adapter";
import { isOrderableCast } from "../helpers/combinations";
import { i18n } from "../helpers/i18n";
import { toOdds } from "../formatters/odds-adapter";

import {
  buildExchangeTransactionalError,
  buildMarketNotification,
  buildPriceNotification,
  buildRacingTitle,
  buildSizeNotification,
  getCastOrdinal,
} from "./betslip-formatters";
import { buildSelection, translateMultiple } from "./connected-sportsbook-betslip-mapper";
import type {
  BetslipLocalization,
  BetslipOddsDisplay,
  BetslipQuickStake,
  ExchangeCurrencyDetails,
  SportsbookBetslipBalance,
} from "../contract";

export const isNotification = (n: AlertProps | undefined): n is AlertProps => !!n;

export const getPriceSnapValue = (validation: PriceValidation, currentPrice?: number): number | undefined => {
  switch (validation.reason) {
    case "ABOVE_MAX_PRICE": {
      return validation.maximum;
    }
    case "INVALID_STEP": {
      if (!currentPrice) {
        return undefined;
      }
      const { nextStep, previousStep } = validation;
      const isNextStepNearTheCurrentPrice = Math.abs(nextStep - currentPrice) < Math.abs(previousStep - currentPrice);
      return isNextStepNearTheCurrentPrice ? nextStep : previousStep;
    }
    case "BELOW_MIN_PRICE": {
      return validation.minimum;
    }
    default: {
      // something went really wrong
      return undefined;
    }
  }
};

export const buildQuickStakesSelector = () =>
  createSelector(
    [
      (quickStakes: BetslipQuickStake[]) => quickStakes,
      (_quickStakes: BetslipQuickStake[], localization: BetslipLocalization) => localization,
    ],
    (quickStakes, localization) =>
      quickStakes.map(({ stake }) => ({
        stake,
        displayStake: `+ ${currencyFormatWithoutDecimalPlaces({ ...localization, value: stake })}`,
      })),
  );

export type DemonstrationCombination = {
  odds: string;
  betType: string;
  translatedBetType: string;
  stake: string;
  potentialReturns: string;
};

export type DemonstrationCombinationProps = {
  combination: BettingState.Combination;
  localization: BetslipLocalization;
  oddsDisplay: BetslipOddsDisplay;
};

export const createBuildDemonstrationCombination = (): OutputParametricSelector<
  ApplicationState,
  DemonstrationCombinationProps,
  DemonstrationCombination | undefined,
  (
    localization: BetslipLocalization,
    oddsDisplay: BetslipOddsDisplay,
    combination: BettingState.Combination,
  ) => DemonstrationCombination | undefined
> => {
  return createSelector(
    [
      (_: ApplicationState, { localization }: DemonstrationCombinationProps) => localization,
      (_: ApplicationState, { oddsDisplay }: DemonstrationCombinationProps) => oddsDisplay,
      (_: ApplicationState, { combination }: DemonstrationCombinationProps) => combination,
    ],
    (localization, oddsDisplay, combination): DemonstrationCombination | undefined => {
      if (!combination?.betType || !combination?.displayOdds || !combination?.potentialReturns) {
        return undefined;
      }

      const { totalStake, displayOdds, potentialReturns } = combination;
      const formattedStake = currencyFormatWithDecimalPlaces({
        ...localization,
        value: totalStake,
        decimalPlaces: 2,
      });
      const formattedReturns = currencyFormatWithDecimalPlaces({
        ...localization,
        value: potentialReturns,
        decimalPlaces: 2,
      });

      return {
        stake: formattedStake,
        betType: combination.betType,
        translatedBetType: translateMultiple(combination.betType),
        odds: formatOdds(toOdds(displayOdds), oddsDisplay),
        potentialReturns: formattedReturns,
      };
    },
  );
};

const getBalanceAvailable = (_state: ApplicationState, balance: SportsbookBetslipBalance) => balance.available;

const getBalanceBonusWagering = (_state: ApplicationState, balance: SportsbookBetslipBalance) => balance.bonusWagering;

export const createGetSbkIsDepositRequiredSelector = () =>
  createSelector(
    [
      getBalanceAvailable,
      getBalanceBonusWagering,
      (_state: ApplicationState, _balance: SportsbookBetslipBalance, stake?: number) => stake,
      getSportsbookBettingState,
    ],
    (available, bonusWagering, stake, bettingState) => {
      const { isBonusSelected, totalStake } = bettingState;

      if (available === null || bonusWagering === null) {
        return false;
      }

      const stakeToEvaluate = stake ?? totalStake;

      return !isBonusSelected && Number(stakeToEvaluate.toFixed(2)) > available + bonusWagering;
    },
  );

export const createGetSbkRequiredDepositValueSelector = () => {
  const isDepositRequiredSelector = createGetSbkIsDepositRequiredSelector();

  return createSelector(
    [getSportsbookBettingState, isDepositRequiredSelector, getBalanceAvailable],
    (bettingState, isDepositRequired, available) => {
      if (!isDepositRequired || available === null) {
        return 0;
      }

      const stake = Number(bettingState.totalStake.toFixed(2));
      return stake > available ? Math.ceil(stake - available) : 0;
    },
  );
};

export const createGetObbIsDepositRequiredSelector = () =>
  createSelector([getObbBettingState, getBalanceAvailable], (obbBettingState, available) => {
    const totalStake = obbBettingState.totalStake || 0;

    if (available === null) {
      return false;
    }

    return Number(totalStake.toFixed(2)) > available;
  });

export const buildBaseCastRunner = (
  metadata: BettingMetadata,
  localization: BetslipLocalization,
): CastBetRunnerSelectionProps => {
  const { title, icon, racingSport, meetingCountry, silkFallbackType, trap } = buildSelection(metadata, localization);

  return {
    horse: title,
    icon,
    racingSport,
    trap,
    meetingCountry,
    silkFallbackType,
  };
};

export const buildOrderedCastRunner = (
  runner: BettingState.Runner,
  metadata: BettingMetadata,
  localization: BetslipLocalization,
): ForecastTricastSelectionProps => ({
  ...buildBaseCastRunner(metadata, localization),
  position: runner.order !== null ? runner.order : undefined,
  positionOrdinal: runner.order ? getCastOrdinal(runner.order) : undefined,
});

export const generateCastTypes = (legs: BettingState.LegsMap, castGroup: CastGroup): CastBetCastTypeItem[] => {
  const { combinations } = castGroup;
  return combinations.map(({ id, legs: [legId] }) => {
    const leg = legs[legId];

    return {
      id,
      text: i18n({ key: `I18N.BETSLIP.SBK.CAST.${leg.legType}` }),
    };
  });
};

export type CastBet = {
  id: string;
  title: string;
  selectedCastType: string;
  isOrderable?: boolean;
};

export const buildCastBet = (
  castGroup: CastGroup,
  context: BetslipCastContext,
  bettingState: BettingState.Group | BetslipSportsbookConfirmationBet,
  metadata: RunnersMetadata,
  localization: BetslipLocalization,
  isOrderable = true,
): CastBet | undefined => {
  const { id: groupId, combinations: castCombinations, metadataRunnerId } = castGroup;
  const { legs, runners } = bettingState;
  const currentCastContext = context[groupId];
  const combination = castCombinations.find(({ id }) => currentCastContext === id);

  if (!combination) {
    return undefined;
  }

  const combinationMetadata = metadata[metadataRunnerId];
  const castRunnerIds = legs[combination.legs[0]]?.runners ?? [];
  const isOrderableCombination = isOrderableCast(combination, legs);
  const isOrderableRunner =
    !!castRunnerIds.length && castRunnerIds.every((runnerId) => runners[runnerId].order !== null);

  return {
    id: groupId,
    title: buildRacingTitle(combinationMetadata, localization),
    selectedCastType: currentCastContext,
    isOrderable: isOrderable && isOrderableRunner && isOrderableCombination,
  };
};

export const createGetCastBetSelector = () => {
  const getCastGroup = createGetCastGroupSelector();

  return createSelector(
    [
      (state: BetslipApplicationState, castGroupId: string) => getCastGroup(state, castGroupId),
      (state: BetslipApplicationState) => getBetslipCastContext(state),
      getSportsbookBettingState,
      (state: BetslipApplicationState) => getBettingResolvers(getBetslipGroup(state)).getMetadata(state),
      (_state: ApplicationState, _castGroupId: string, localization: BetslipLocalization) => localization,
    ],
    (castGroup, castContext, bettingState, metadata, localization) => {
      const context = castContext || { [castGroup.id]: castGroup.combinations[0].id };

      return buildCastBet(castGroup, context, bettingState, metadata, localization);
    },
  );
};

export const getPriceBoostCount = (
  combinations: { isPriceBoostSelected: boolean }[],
  boostWallet: { amount: number } | null | undefined,
): number => {
  if (!boostWallet) {
    return 0;
  }

  return combinations.reduce(
    (count, combination) => count - (combination.isPriceBoostSelected ? 1 : 0),
    boostWallet.amount,
  );
};

export const isAnyPriceBoostAvailable = (combinations: { isPriceBoostAvailable: boolean }[]): boolean =>
  combinations.some((c) => c.isPriceBoostAvailable);

type Notification = {
  notification: AlertProps;
};

export const buildMarketError = (marketStatus: BetslipExchangeMarketStatus): Notification | undefined => {
  if (marketStatus !== "OPEN") {
    return {
      notification: buildMarketNotification(marketStatus),
    };
  }
  return undefined;
};

export const buildPlaceError = (
  marketStatus: BetslipExchangeMarketStatus,
  exchangePlaceError?: ExchangeBetTransactionError,
  currencySymbol?: string,
): Notification | undefined => {
  if (marketStatus === "OPEN") {
    return exchangePlaceError
      ? { notification: buildExchangeTransactionalError(exchangePlaceError, currencySymbol) }
      : undefined;
  }

  return undefined;
};

type PriceNotification = {
  newPrice: number;
} & Notification;

export const buildPriceError = (
  marketStatus: BetslipExchangeMarketStatus,
  priceValidation?: PriceValidation,
  price?: number,
): PriceNotification | undefined => {
  if (marketStatus === "OPEN" && priceValidation) {
    const newPrice = getPriceSnapValue(priceValidation, price);
    return newPrice
      ? {
          newPrice,
          notification: buildPriceNotification(priceValidation),
        }
      : undefined;
  }
  return undefined;
};

type SizeNotification = {
  newSize: number;
} & Notification;

export const buildSizeError = (
  marketStatus: BetslipExchangeMarketStatus,
  localization: BetslipLocalization,
  currencyDetails: ExchangeCurrencyDetails,
  sizeValidation?: SizeValidation,
): SizeNotification | undefined => {
  if (marketStatus === "OPEN" && sizeValidation) {
    const newSize = currencyDetails.minStake || LADDER_DEFAULT_MIN_SIZE;
    const formattedNewSize = currencyFormatWithDecimalPlaces({
      ...localization,
      value: newSize,
      decimalPlaces: 2,
    });

    return {
      newSize,
      notification: buildSizeNotification(sizeValidation, formattedNewSize),
    };
  }
  return undefined;
};
